import { DateTime } from "luxon";
import * as R from "ramda";

import Schedule from "api/types/Schedule";
import { DaysOfWeek } from "gql/types/globals";

import excludeFromIntervals from "./excludeFromIntervals";
import mergeIntervals from "./mergeIntervals";
import timeStringToNumber from "./timeStringToNumber";

import Interval from "../utility/Interval";

const DATE_TIME_MIN = DateTime.fromISO("0001-01-01");
const DATE_TIME_MAX = DateTime.fromISO("9999-01-01");

const DAYS_OF_WEEK = [
	DaysOfWeek.mon,
	DaysOfWeek.tue,
	DaysOfWeek.wed,
	DaysOfWeek.thu,
	DaysOfWeek.fri,
	DaysOfWeek.sat,
	DaysOfWeek.sun
];

/**
 * Converts schedule into a per-date list of intervals accounting for time zone and exceptions
 */
export default function* unwrapSchedule({
	schedule,
	tz,
	start
}: {
	schedule: Schedule;
	tz: string;
	start?: string | "today";
}): Generator<{
	date: string;
	intervals: {
		startsAt: DateTime;
		endsAt: DateTime;
	}[];
}> {
	const startAt =
		!start || start === "today"
			? DateTime.utc().setZone(tz).startOf("day")
			: DateTime.fromISO(start, { zone: tz }).startOf("day");

	for (let date = startAt; ; date = date.plus({ days: 1 })) {
		const dayOfWeek = DAYS_OF_WEEK[date.weekday - 1];

		let intervals = mergeIntervals(
			schedule.hours
				.filter(x => x.daysOfWeek.includes(dayOfWeek))
				.map(x => x.intervals)
				.reduce((res, item) => {
					res.push(...item);
					return res;
				}, [])
				.map<Interval<number>>(({ startTime, endTime }) => [timeStringToNumber(startTime), timeStringToNumber(endTime)])
		).map<Interval<DateTime>>(([startTime, endTime]) => [
			R.isNil(startTime) ? startTime : date.plus({ milliseconds: startTime }),
			R.isNil(endTime) ? endTime : date.plus({ milliseconds: endTime })
		]);

		const exceptions = mergeIntervals(
			(schedule.exceptions || [])
				.map(x => ({
					startsAt: x.startsAt ? DateTime.fromISO(x.startsAt) : null,
					endsAt: x.endsAt ? DateTime.fromISO(x.endsAt) : null
				}))
				.filter(x => (!x.startsAt || +x.startsAt < +date.plus({ days: 1 })) && (!x.endsAt || +x.endsAt > +date))
				.map<Interval<DateTime>>(x => [x.startsAt, x.endsAt])
		);

		if (exceptions.length > 0) {
			intervals = excludeFromIntervals(intervals, exceptions);
		}

		yield {
			date: date.toISODate(),
			intervals: intervals.map(([startsAt, endsAt]) => ({
				startsAt: startsAt || DATE_TIME_MIN,
				endsAt: endsAt || DATE_TIME_MAX
			}))
		};
	}
}
